// ═══════════════════════════════════════════════════════════════════════
// Temps › Calendrier d'équipe (agent H ; conception § 14.3).
// Vue mois ou semaine. La FORME double la couleur : plein = validé, hachuré = en attente,
// barré = refusé ; légende toujours visible. Les congés en attente apparaissent avant qu'on les
// accepte. La maladie n'est jamais nommée (« Absence déclarée »). Un clic sur un jour ouvre le
// détail de la journée du collaborateur.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, EtatVide, Squelette } from '../../design/composants';
import JourneeDetailModal from './JourneeDetailModal';
import { NavEquipe, lireEquipe, messageErreur, codeErreur, aujourdhuiIso, nomComplet, useDroitsEquipe } from './EquipeCommun';

// Trois couleurs au plus (couleurs limitées) ; la forme porte l'état.
const COULEURS = { CONGE: 'var(--sg-primaire)', ABSENCE_DECLAREE: 'var(--sg-texte-3)' };
const couleurDe = (famille) => COULEURS[famille] || 'var(--sg-info)';

function styleEtat(etat, couleur) {
  if (etat === 'VALIDE') return { background: couleur, border: `1px solid ${couleur}` };
  if (etat === 'EN_ATTENTE') {
    return { background: `repeating-linear-gradient(135deg, ${couleur} 0 3px, transparent 3px 7px)`, border: `1px solid ${couleur}` };
  }
  // Refusé : contour seul, barré d'une diagonale.
  return { background: `linear-gradient(to top right, transparent calc(50% - 1px), ${couleur} calc(50% - 1px), ${couleur} calc(50% + 1px), transparent calc(50% + 1px))`, border: `1px dashed ${couleur}`, opacity: 0.8 };
}
const LIBELLES_ETAT = { VALIDE: 'validé', EN_ATTENTE: 'en attente', REFUSE: 'refusé' };

function Pastille({ etat, couleur = 'var(--sg-texte-2)', taille = 14 }) {
  return <span aria-hidden="true" style={{ display: 'inline-block', width: taille, height: taille, borderRadius: 3, ...styleEtat(etat, couleur) }} />;
}

const ajouter = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const lundi = (iso) => { const d = new Date(`${iso}T12:00:00Z`); const j = (d.getUTCDay() + 6) % 7; return ajouter(iso, -j); };
const moisSuivant = (m, n) => { const d = new Date(`${m}-15T12:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7); };
const estWeekEnd = (iso) => [0, 6].includes(new Date(`${iso}T12:00:00Z`).getUTCDay());
const JOURS_COURTS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

export default function CalendrierEquipePage() {
  const droits = useDroitsEquipe();
  const [mode, setMode] = useState('mois');
  const [mois, setMois] = useState(aujourdhuiIso().slice(0, 7));
  const [semaine, setSemaine] = useState(lundi(aujourdhuiIso()));
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [refus, setRefus] = useState(false);
  const [journee, setJournee] = useState(null);

  const charger = useCallback(async () => {
    try {
      setErreur(''); setRefus(false);
      setD(await lireEquipe('/calendrier', mode === 'mois' ? { mois } : { semaine }));
    } catch (e) {
      setD(null);
      if (e?.response?.status === 403) setRefus(codeErreur(e) || true);
      setErreur(messageErreur(e, 'Chargement impossible.'));
    }
  }, [mode, mois, semaine]);
  useEffect(() => { charger(); }, [charger]);

  // Entrées par salarié et par jour (une case peut porter plusieurs entrées : on garde la plus « forte »).
  const cases = useMemo(() => {
    const m = new Map();
    const rang = { VALIDE: 3, EN_ATTENTE: 2, REFUSE: 1 };
    for (const e of d?.entrees || []) {
      for (const jour of d.jours) {
        if (jour < e.date_debut || jour > e.date_fin) continue;
        const cle = `${e.employe_id}|${jour}`;
        const deja = m.get(cle);
        if (!deja || rang[e.etat] > rang[deja.etat]) m.set(cle, e);
      }
    }
    return m;
  }, [d]);

  const precedent = () => (mode === 'mois' ? setMois(moisSuivant(mois, -1)) : setSemaine(ajouter(semaine, -7)));
  const suivant = () => (mode === 'mois' ? setMois(moisSuivant(mois, 1)) : setSemaine(ajouter(semaine, 7)));
  const titrePeriode = mode === 'mois'
    ? new Date(`${mois}-15T12:00:00`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    : `Semaine du ${new Date(`${semaine}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
  const auj = aujourdhuiIso();

  return (
    <Layout>
      <div className="rh-page rh-page-large">
        <EnTetePage fil={[{ libelle: 'Temps' }, { libelle: 'Mon équipe', vers: '/temps/equipe' }, { libelle: 'Calendrier' }]} titre="Calendrier d'équipe"
          sousTitre="Congés, permissions, missions et absences de vos collaborateurs. Les demandes en attente apparaissent avant votre décision." />
        <NavEquipe actif="calendrier" compteur={droits?.compteur_a_traiter} />

        {refus && <EtatVide Icone={CalendarDays} titre="Aucune équipe à afficher" texte={erreur} />}
        {!refus && erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}

        {!refus && (
          <Carte>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <div className="rh-vues" role="group" aria-label="Affichage">
                <button type="button" className="rh-vue" aria-pressed={mode === 'mois'} onClick={() => setMode('mois')}>Mois</button>
                <button type="button" className="rh-vue" aria-pressed={mode === 'semaine'} onClick={() => setMode('semaine')}>Semaine</button>
              </div>
              <Bouton petit variante="discret" Icone={ChevronLeft} onClick={precedent} aria-label="Période précédente" />
              <strong style={{ fontWeight: 600, minWidth: 150, textAlign: 'center', textTransform: 'capitalize' }}>{titrePeriode}</strong>
              <Bouton petit variante="discret" Icone={ChevronRight} onClick={suivant} aria-label="Période suivante" />
              <span style={{ flex: 1 }} />
              {/* Légende visible : la forme double la couleur. */}
              <div aria-label="Légende" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, color: 'var(--sg-texte-2)' }}>
                {(d?.legende || [{ etat: 'VALIDE', libelle: 'Validé', forme: 'plein' }, { etat: 'EN_ATTENTE', libelle: 'En attente', forme: 'hachuré' }, { etat: 'REFUSE', libelle: 'Refusé', forme: 'barré' }]).map((l) => (
                  <span key={l.etat} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Pastille etat={l.etat} />{l.libelle} ({l.forme})</span>
                ))}
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Pastille etat="VALIDE" couleur={COULEURS.CONGE} />Congé</span>
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Pastille etat="VALIDE" couleur="var(--sg-info)" />Permission, mission, autre</span>
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><Pastille etat="VALIDE" couleur={COULEURS.ABSENCE_DECLAREE} />Absence déclarée</span>
              </div>
            </div>

            {!d ? <Squelette largeur="80%" hauteur={120} /> : d.employes.length === 0 ? (
              <EtatVide Icone={CalendarDays} titre="Aucun collaborateur" />
            ) : (
              <div className="rh-conteneur-tableau" style={{ overflowX: 'auto' }}>
                <table className="rh-tableau" style={{ minWidth: mode === 'mois' ? 900 : 560, tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 170, position: 'sticky', left: 0, background: 'var(--sg-surface)', zIndex: 1 }}>Collaborateur</th>
                      {d.jours.map((j) => {
                        const x = new Date(`${j}T12:00:00Z`);
                        return (
                          <th key={j} style={{ padding: '6px 2px', textAlign: 'center', background: estWeekEnd(j) ? 'var(--sg-surface-2)' : undefined, color: j === auj ? 'var(--sg-primaire)' : undefined }}
                            title={x.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}>
                            <span style={{ display: 'block', fontSize: 11 }}>{JOURS_COURTS[x.getUTCDay()]}</span>{x.getUTCDate()}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {d.employes.map((e) => (
                      <tr key={e.id}>
                        <td style={{ position: 'sticky', left: 0, background: 'var(--sg-surface)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', zIndex: 1 }}>{nomComplet(e)}</td>
                        {d.jours.map((j) => {
                          const entree = cases.get(`${e.id}|${j}`);
                          const dateLisible = new Date(`${j}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
                          const titre = entree ? `${nomComplet(e)} — ${dateLisible} — ${entree.libelle} (${LIBELLES_ETAT[entree.etat]})` : `${nomComplet(e)} — ${dateLisible} — rien de particulier`;
                          return (
                            <td key={j} style={{ padding: 2, background: estWeekEnd(j) ? 'var(--sg-surface-2)' : undefined }}>
                              <button type="button" onClick={() => setJournee({ employeId: e.id, date: j })} title={titre} aria-label={titre}
                                style={{ display: 'block', width: '100%', height: 24, borderRadius: 4, cursor: 'pointer', padding: 0,
                                  ...(entree ? styleEtat(entree.etat, couleurDe(entree.famille)) : { background: 'transparent', border: '1px solid transparent' }) }} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Carte>
        )}

        {journee && <JourneeDetailModal employeId={journee.employeId} date={journee.date} onFermer={() => { setJournee(null); charger(); }} />}
      </div>
    </Layout>
  );
}
